import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../../shared/context/auth-context';
import { HeaderNavigation } from '../../shared/headerNavigation/HeaderNavigation';
import { useUserData } from '../../shared/hooks/useUserData';
import './ProfileTabNavigation.css';
import { navigation } from './navigation';

const TAB_IDS = {
  INVITE: 2,
  ASSESSMENT: 3,
  LEADERSHIP_HUB: 4,
  PANEL: 5,
};

const USER_ACCESS = {
  FRIEND_FAMILY: '2',
  LEADER: '3',
  CHILD_CLIENT: '4',
  PARENT_CLIENT: '5',
  ADMIN: '10',
};

export function ProfileTabNavigation({ activePage, setActivePage }) {
  const auth = useContext(AuthContext);
  const { userData } = useUserData();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toggleActivePage = (tabId) => {
    const navigationPaths = {
      [TAB_IDS.ASSESSMENT]: '/assessment',
      [TAB_IDS.LEADERSHIP_HUB]: '/company-report/live-analytics',
    };

    const externalLinks = {
      [TAB_IDS.PANEL]: '/panel/clients',
    };

    if (navigationPaths[tabId]) {
      navigate(navigationPaths[tabId]);
    } else if (externalLinks[tabId]) {
      window.open(externalLinks[tabId], '_blank');
    } else {
      setActivePage(navigation[tabId]);
      navigate(`/profile/${navigation[tabId].title}`);
    }
  };

  const shouldHideTab = useCallback(
    (index) => {
      const isUserFriendFamily = auth.access === USER_ACCESS.FRIEND_FAMILY;
      const isUserAdmin = auth.access === USER_ACCESS.ADMIN;
      const isUserLeader = auth.access === USER_ACCESS.LEADER;
      const isUserChildClient = auth.access === USER_ACCESS.CHILD_CLIENT;
      const isUserParentClient = auth.access === USER_ACCESS.PARENT_CLIENT;
      const hasTags = auth.tags.length > 0;
      const isUserHub = auth.isHub;
      const inviteFriendsDisabled = isUserFriendFamily || isUserHub || userData?.fnfDisabled;
      const hasTooLowLevelToSeePanel = !(isUserParentClient || isUserChildClient || isUserAdmin);

      if (index === TAB_IDS.INVITE && inviteFriendsDisabled) return true;
      if (index === TAB_IDS.ASSESSMENT && !(isUserAdmin || !hasTags)) return true;
      if (
        index === TAB_IDS.LEADERSHIP_HUB &&
        (isUserHub || !(isUserLeader || isUserAdmin || isUserChildClient || isUserChildClient))
      )
        return true;
      if (index === TAB_IDS.PANEL && hasTooLowLevelToSeePanel) return true;
      return false;
    },
    [auth.access, auth.isHub, auth.tags.length, userData?.fnfDisabled]
  );

  const navigationItems = navigation
    .map((item, index) => {
      if (shouldHideTab(index)) return undefined;

      return {
        id: item.target,
        code: item.target,
        label: t(item.code),
        type: 'button',
        isActive: activePage === item,
        isVisible: true,
        onClick: () => toggleActivePage(index),
      };
    })
    .filter((item) => item !== undefined);

  return <HeaderNavigation profilePageItems={navigationItems} />;
}
